import { defineStore } from 'pinia';
import { LegalApiClient } from '../api/legal';
import { toastService } from '../services/toastService';
import { localeService } from '../services/localeService';
import type { LegalDocument, ConsentStatus } from '../types/legal';

interface LegalState {
    documents: Record<string, LegalDocument>;
    consentStatuses: Record<string, ConsentStatus>;
    userConsents: Array<{
        id: number;
        documentSlug: string;
        documentTitle: string;
        version: number;
        acceptedAt: string;
        locale: string;
        checksum: string;
    }>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
}

export const useLegalStore = defineStore('legal', {
    state: (): LegalState => ({
        documents: {},
        consentStatuses: {},
        userConsents: [],
        loading: {},
        error: {}
    }),

    getters: {
        getDocumentBySlug: (state) => (slug: string): LegalDocument | undefined => {
            return state.documents[slug];
        },

        getConsentStatusBySlug: (state) => (slug: string): ConsentStatus | undefined => {
            return state.consentStatuses[slug];
        },

        isLoading: (state) => (key: string): boolean => {
            return !!state.loading[key];
        },

        getError: (state) => (key: string): string | null => {
            return state.error[key] || null;
        }
    },

    actions: {
        /**
         * Load legal document by slug
         */
        async loadDocument(slug: string, locale?: string): Promise<LegalDocument | null> {
            const key = `document-${slug}`;
            this.loading[key] = true;
            this.error[key] = null;

            try {
                // Use locale service to determine locale with fallback
                const actualLocale = localeService.getLocaleWithFallback(locale);
                const document = await LegalApiClient.getLegal(slug, actualLocale);
                if (document) {
                    this.documents[slug] = document;
                }
                return document;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                toastService.showError(`Error loading document: ${errorMsg}`);
                return null;
            } finally {
                this.loading[key] = false;
            }
        },

        /**
         * Get user's consent status for document
         */
        async checkConsentStatus(slug: string, locale?: string): Promise<ConsentStatus> {
            const key = `consent-${slug}`;
            this.loading[key] = true;
            this.error[key] = null;

            try {
                // Use locale service to determine locale with fallback
                const actualLocale = localeService.getLocaleWithFallback(locale);
                const status = await LegalApiClient.getConsentStatus(slug, actualLocale);
                this.consentStatuses[slug] = status;
                return status;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                toastService.showError(`Error checking consent status: ${errorMsg}`);
                return { hasConsented: false };
            } finally {
                this.loading[key] = false;
            }
        },

        /**
         * Submit user consent for document
         */
        async giveConsent(slug: string, versionId: number, meta: { ip: string; userAgent: string }) {
            const key = `consent-${slug}`;
            this.loading[key] = true;
            this.error[key] = null;

            try {
                const result = await LegalApiClient.postConsent(slug, versionId, meta);
                // Update consent status
                this.consentStatuses[slug] = {
                    hasConsented: true,
                    consentId: result.consent_id,
                    consentedAt: result.accepted_at
                };
                toastService.showSuccess('Consent successfully submitted');
                return result;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                toastService.showError(`Error submitting consent: ${errorMsg}`);
                throw error;
            } finally {
                this.loading[key] = false;
            }
        },

        /**
         * Acknowledge consent with current document version
         */
        async acknowledgeCurrent(slug: string, locale?: string) {
            const key = `ack-${slug}`;
            this.loading[key] = true;
            this.error[key] = null;

            try {
                // Use locale service to determine locale with fallback
                const actualLocale = localeService.getLocaleWithFallback(locale);
                const result = await LegalApiClient.ackCurrent(slug, actualLocale);
                // Update consent status
                this.consentStatuses[slug] = {
                    hasConsented: true,
                    consentId: result.consent_id,
                    consentedAt: result.accepted_at
                };
                toastService.showSuccess('Consent successfully submitted');
                return result;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                // Check if error is a conflict (user already consented)
                if (!errorMsg.includes('Conflict: user has already consented')) {
                    toastService.showError(`Error submitting consent: ${errorMsg}`);
                }
                throw error;
            } finally {
                this.loading[key] = false;
            }
        },

        /**
         * Get all consents of current user
         */
        async loadUserConsents() {
            const key = 'user-consents';
            this.loading[key] = true;
            this.error[key] = null;

            try {
                const consents = await LegalApiClient.getMyConsents();
                this.userConsents = consents;
                return consents;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                toastService.showError(`Error loading consents: ${errorMsg}`);
                throw error;
            } finally {
                this.loading[key] = false;
            }
        },

        /**
         * Check user consent status for specific document
         */
        async getUserConsentStatus(slug: string): Promise<{ hasConsented: boolean }> {
            const key = `user-consent-status-${slug}`;
            this.loading[key] = true;
            this.error[key] = null;

            try {
                const result = await LegalApiClient.getUserConsentStatus(slug);
                return result;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                toastService.showError(`Error checking consent status for ${slug}: ${errorMsg}`);
                return { hasConsented: false };
            } finally {
                this.loading[key] = false;
            }
        },

        /**
         * Get list of all user consents
         */
        async getMyConsents(slug?: string): Promise<Array<{
            id: number;
            documentSlug: string;
            documentTitle: string;
            version: number;
            acceptedAt: string;
            locale: string;
            checksum: string;
        }>> {
            const key = 'user-consents';
            this.loading[key] = true;
            this.error[key] = null;

            try {
                const result = await LegalApiClient.getMyConsents(slug);
                this.userConsents = result;
                return result;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                toastService.showError(`Error retrieving consent list: ${errorMsg}`);
                return [];
            } finally {
                this.loading[key] = false;
            }
        },

        /**
         * Clear errors for key
         */
        clearError(key: string) {
            this.error[key] = null;
        },

        /**
         * Get information about required re-consent
         */
        async getReconsentInfo(): Promise<import('../types/legal').ReconsentStatus> {
            const key = 'reconsent-info';
            this.loading[key] = true;
            this.error[key] = null;

            try {
                const result = await LegalApiClient.getReconsentInfo();
                return result;
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                this.error[key] = errorMsg;
                toastService.showError(`Error checking re-consent status: ${errorMsg}`);
                return { needsReconsent: false };
            } finally {
                this.loading[key] = false;
            }
        }
    }
});